import { Component, useState, type ErrorInfo, type ReactNode } from 'react';
import { useI18n } from '../lib/i18n';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

function ErrorFallback({
  error,
  onReset,
}: {
  error: Error | null;
  onReset: () => void;
}) {
  const { t } = useI18n();
  const [showDetails, setShowDetails] = useState(false);

  return (
    <div className="flex min-h-[400px] w-full items-center justify-center p-6">
      <div className="w-full max-w-md rounded-2xl border border-line bg-white p-6 shadow-xl dark:border-slate-800 dark:bg-slate-900 text-center">
        <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-2xl bg-danger-50 text-2xl text-danger dark:bg-rose-950/50 dark:text-rose-400 shadow-sm">
          ⚠️
        </div>

        <h2 className="text-lg font-bold text-ink dark:text-slate-100">
          {t('error.boundary.title') || 'Something went wrong'}
        </h2>

        <p className="mt-1.5 text-xs leading-relaxed text-ink-3 dark:text-slate-400">
          {t('error.boundary.desc') ||
            'An unexpected error occurred while rendering this page. You can try refreshing or returning to the dashboard.'}
        </p>

        <div className="mt-5 flex flex-wrap items-center justify-center gap-2.5">
          <button
            type="button"
            onClick={onReset}
            className="rounded-xl bg-brand px-4 py-2 text-xs font-bold text-white shadow-sm transition hover:bg-brand-dark hover:shadow active:scale-95"
          >
            {t('error.boundary.retry') || 'Try again'}
          </button>

          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-xl border border-line bg-white px-3.5 py-2 text-xs font-semibold text-ink-2 shadow-xs transition hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-750"
          >
            {t('error.boundary.reload') || 'Reload page'}
          </button>

          <button
            type="button"
            onClick={() => {
              window.location.href = '/';
            }}
            className="rounded-xl border border-line bg-white px-3.5 py-2 text-xs font-semibold text-ink-2 shadow-xs transition hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-750"
          >
            {t('error.boundary.dashboard') || 'Dashboard'}
          </button>
        </div>

        {/* Expandable Technical Details */}
        {error && (
          <div className="mt-5 border-t border-line/60 pt-3 text-start dark:border-slate-800/60">
            <button
              type="button"
              onClick={() => setShowDetails((prev) => !prev)}
              className="text-[11px] font-semibold text-ink-4 hover:text-ink-2 dark:text-slate-500 dark:hover:text-slate-300"
            >
              {showDetails
                ? t('error.boundary.hideDetails') || 'Hide details ▴'
                : t('error.boundary.showDetails') || 'Show technical error details ▾'}
            </button>

            {showDetails && (
              <pre className="mt-2 max-h-40 overflow-auto rounded-lg bg-slate-900 p-3 font-mono text-[10px] text-rose-300 leading-relaxed dark:bg-slate-950">
                {error.toString()}
                {'\n'}
                {error.stack}
              </pre>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[WaTag Global ErrorBoundary caught error]:', error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return <ErrorFallback error={this.state.error} onReset={this.handleReset} />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
